import PropTypes from 'prop-types';
import { useQuery } from '@tanstack/react-query';

import {
  getPurchaseAmendmentKindConfig,
  PURCHASE_AMENDMENT_KIND,
} from '#/modules/srmMgmt/purchaseOrder/helper/purchaseAmendmentKinds';
import { PAYMENT_DATA_STATUS } from '#/modules/srmMgmt/purchaseOrder/helper/paymentPlanForm';
import PurchasePaymentPhasesTable from '#/modules/srmMgmt/purchaseOrder/components/PurchasePaymentPhasesTable';
import PurchasePaymentRecordsSection from '#/modules/srmMgmt/purchaseOrder/components/PurchasePaymentRecordsSection';
import { PERM } from '#/config/permissions';
import { useCan } from '#/modules/infra/auth/useCan';
import { formatDisplayAmount } from '#/utils/formatNumber';
import { purchasePaymentPlanQueryKey } from '#/utils/queryKeys';

function isNotFound(error) {
  return error?.response?.status === 404;
}

/**
 * Detail「付款計畫」頁籤（物料／非庫存採購共用）：GET `…/payment-plan` 唯讀。
 * 404＝尚無計畫（不可推定已付／未付）；`HISTORICAL_UNKNOWN` 已付欄顯示「付款資料未登錄」。
 * 改計畫只能走異動單。具 `fms.purchase_payment.read` 才在下方顯示財務付款紀錄。
 */
const PurchaseOrderPaymentPlanSection = ({
  kind = PURCHASE_AMENDMENT_KIND.PURCHASE_ORDER,
  orderId,
}) => {
  const config = getPurchaseAmendmentKindConfig(kind);
  const canReadPayments = useCan(PERM.FMS_PURCHASE_PAYMENT_READ);
  const planQuery = useQuery({
    queryKey: purchasePaymentPlanQueryKey(kind, orderId),
    queryFn: () => config.fetchPaymentPlan(orderId),
    enabled: Boolean(orderId),
    retry: false,
  });

  if (planQuery.isLoading) {
    return <p className="px-4 py-5 text-sm text-muted-foreground">讀取中…</p>;
  }

  if (planQuery.isError) {
    return (
      <p className="px-4 py-5 text-sm text-muted-foreground">
        {isNotFound(planQuery.error)
          ? '尚無付款計畫。草稿提交或核准時系統會建立預設計畫；舊單可能尚未建立，無法據此判斷已付或未付。'
          : config.getErrorMessage(planQuery.error, '讀取付款計畫失敗')}
      </p>
    );
  }

  const plan = planQuery.data?.data ?? null;
  const paymentDataUnknown = plan?.paymentDataStatus === PAYMENT_DATA_STATUS.HISTORICAL_UNKNOWN;

  return (
    <div className="space-y-3 px-4 py-4">
      <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm text-gray-700">
        {plan?.versionNo != null ? <span>{`版本 第 ${plan.versionNo} 版`}</span> : null}
        {plan?.purchaseTotalAmount != null ? (
          <span className="tabular-nums">{`應付總額 ${formatDisplayAmount(plan.purchaseTotalAmount)}`}</span>
        ) : null}
      </div>
      {paymentDataUnknown ? (
        <p className="text-sm text-amber-700">歷史付款資料尚未登錄，已付金額暫不顯示，不代表尚未付款。</p>
      ) : null}
      <PurchasePaymentPhasesTable
        phases={plan?.phases ?? []}
        paymentDataUnknown={paymentDataUnknown}
      />
      <p className="text-xs text-muted-foreground">核准後如需調整付款計畫，請在「異動歷史」新增異動單。</p>
      {canReadPayments && plan?.accountId ? (
        <PurchasePaymentRecordsSection accountId={plan.accountId} phases={plan.phases ?? []} />
      ) : null}
    </div>
  );
};

PurchaseOrderPaymentPlanSection.propTypes = {
  kind: PropTypes.oneOf(Object.values(PURCHASE_AMENDMENT_KIND)),
  orderId: PropTypes.string,
};

export default PurchaseOrderPaymentPlanSection;
